package dev.lingvo.site.pages.app.projects.slug.screenshots

import dev.lingvo.shared.CoverageCellDto
import dev.lingvo.shared.CoverageDto
import dev.lingvo.shared.CoverageState
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.PutScreenshotPlanRequest
import dev.lingvo.shared.ScreenshotPlanDto
import dev.lingvo.shared.ScreenshotSpecs
import dev.lingvo.shared.SlotInput
import dev.lingvo.shared.StaleReason
import dev.lingvo.shared.StorePlatform
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.i18n.tr
import dev.lingvo.site.i18n.trf
import dev.lingvo.site.i18n.tPlural
import dev.lingvo.site.i18n.I18n
import kotlin.js.Date
import dev.lingvo.site.i18n.t
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.CoroutineStart
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch

/** UI-only states preserve refusal and non-required cells without inventing server enum values. */
enum class ShotCellStatus { CURRENT, STALE, MISSING, NOT_REQUIRED, REFUSED }

/** Keeps the selected device and its authoritative capture together in aggregated views. */
data class ShotGridCell(val status: ShotCellStatus, val device: DeviceClass?, val capture: CoverageCellDto?, val refusal: String? = null)

/** Starts with the required phone class from the shared device inventory. */
fun firstShotDevice(platform: StorePlatform): DeviceClass = DeviceClass.forPlatform(platform)
    .firstOrNull { ScreenshotSpecs.of(it).required } ?: DeviceClass.forPlatform(platform).first()

/** Five intentionally named screens teach a useful plan before any captures exist. */
fun starterShotPlan(platform: StorePlatform): List<SlotInput> = listOf(
    "home" to tr("app.projects.slug.screenshots.plan_model.home", "Home"),
    "main-task" to tr("app.projects.slug.screenshots.plan_model.main_task", "Main task"),
    "detail" to tr("app.projects.slug.screenshots.plan_model.detail", "Detail"),
    "progress" to tr("app.projects.slug.screenshots.plan_model.progress", "Progress"),
    "settings" to tr("app.projects.slug.screenshots.plan_model.settings", "Settings"),
).map { (key, title) -> SlotInput(key, title, devices = listOf(firstShotDevice(platform))) }

/** Retains fields not edited by this screen, including key scope and localized-UI mode. */
fun ScreenshotPlanDto.editableSlots(): List<SlotInput> = slots.sortedBy { it.position }.map {
    SlotInput(it.key, it.title, it.howTo, it.devices, it.accent, it.keys, it.localizedUi,
        demoDataProfile = it.demoDataProfile)
}

/** Every autosave names its exact plan version; a conflict never becomes an unguarded retry. */
fun planEtag(version: Long): String = "\"plan-$version\""

/** Full-plan writes keep the watched files and make pruning an explicit caller decision. */
fun planWrite(plan: ScreenshotPlanDto, slots: List<SlotInput>, prune: Boolean = false): PutScreenshotPlanRequest =
    PutScreenshotPlanRequest(slots, watchFiles = plan.watchFiles, prune = prune)

/** Invalid moves are ignored so a removed row cannot corrupt another row's position. */
fun <T> moveShot(items: List<T>, from: Int, to: Int): List<T> =
    if (from !in items.indices || to !in items.indices || from == to) items
    else items.toMutableList().apply { add(to, removeAt(from)) }

/** A duplicate gets a fresh bounded key while preserving its capture instructions and device scope. */
fun duplicateShot(slots: List<SlotInput>, index: Int): List<SlotInput> {
    val source = slots.getOrNull(index) ?: return slots
    val existing = slots.map { it.key }.toSet()
    val stem = source.key.take(38)
    val key = generateSequence(2) { it + 1 }.map { "$stem-copy-$it" }.first { it !in existing }
    return slots.toMutableList().apply { add(index + 1, source.copy(key = key,
        title = trf("screenshots.plan.copy_title", "{title} copy", "title" to source.title))) }
}

/** The shared store table supplies the limit, including Play's eight-image sets. */
fun shotPlanLimit(platform: StorePlatform): Int = ScreenshotSpecs.of(firstShotDevice(platform)).maxPerLocale

/** Worst-state aggregation keeps a missing device visible even when another device is current. */
fun coverageGridCell(coverage: CoverageDto, slotId: String, locale: String, device: DeviceClass?, refusals: Map<String, String> = emptyMap()): ShotGridCell {
    val cells = coverage.rows.firstOrNull { it.slotId == slotId }?.cells.orEmpty()
        .filter { it.locale == locale && (device == null || it.deviceClass == device) }
    if (cells.isEmpty()) return ShotGridCell(ShotCellStatus.NOT_REQUIRED, device, null)
    cells.firstOrNull { "${slotId}:${locale}:${it.deviceClass.id}" in refusals }?.let { refused ->
        return ShotGridCell(ShotCellStatus.REFUSED, refused.deviceClass, refused, refusals["${slotId}:${locale}:${refused.deviceClass.id}"])
    }
    val worst = cells.maxBy { when (it.state) { CoverageState.CURRENT -> 0; CoverageState.STALE -> 1; CoverageState.MISSING -> 2 } }
    return ShotGridCell(when (worst.state) {
        CoverageState.CURRENT -> ShotCellStatus.CURRENT
        CoverageState.STALE -> ShotCellStatus.STALE
        CoverageState.MISSING -> ShotCellStatus.MISSING
    }, worst.deviceClass, worst)
}

/** Full sentences carry the status and its repair to screen readers, independently of colour and hover. */
fun coverageCellLabel(title: String, locale: String, cell: ShotGridCell, capturedAt: String? = null): String {
    val language = localeInfo(locale).englishName
    val label = when (cell.status) {
        ShotCellStatus.CURRENT -> trf("screenshots.coverage.current", "{slot}, {language}: current.", "slot" to title, "language" to language)
        ShotCellStatus.STALE -> if (cell.capture?.stale?.reasons?.any { it == StaleReason.CAPTURE_INVALIDATED || it == StaleReason.SLOT_INVALIDATED } == true)
            trf("screenshots.coverage.invalidated", "{slot}, {language}: stale. This capture was marked for retake.", "slot" to title, "language" to language)
        else if (cell.capture?.stale?.reasons?.contains(StaleReason.TEXT_CHANGED_DURING_CAPTURE) == true)
            trf("screenshots.coverage.changed_during", "{slot}, {language}: stale. Capture inputs changed while this capture was being taken.", "slot" to title, "language" to language)
        else if (cell.capture?.stale == null || cell.capture.stale!!.let { it.changedKeys + it.addedKeys + it.removedKeys } == 0)
            trf("screenshots.coverage.stale_unknown", "{slot}, {language}: stale. Queue a retake to refresh this capture.", "slot" to title, "language" to language)
        else tPlural("screenshots.coverage.stale", cell.capture?.stale?.let { it.changedKeys + it.addedKeys + it.removedKeys } ?: 0,
            "{slot}, {language}: stale. {count} interface string changed after this capture.",
            "{slot}, {language}: stale. {count} interface strings changed after this capture.", "slot" to title, "language" to language)
        ShotCellStatus.MISSING -> trf("screenshots.coverage.missing", "{slot}, {language}: missing. Wanted {size}.",
            "slot" to title, "language" to language, "size" to (cell.device?.let { ScreenshotSpecs.describe(it.spec.captureRule) }.orEmpty()))
        ShotCellStatus.NOT_REQUIRED -> trf("screenshots.coverage.not_required", "{slot}, {language}: not required.", "slot" to title, "language" to language)
        ShotCellStatus.REFUSED -> trf("screenshots.coverage.refused", "{slot}, {language}: refused. {reason}",
            "slot" to title, "language" to language, "reason" to cell.refusal.orEmpty())
    }
    val date = capturedAt?.let { Date(it) }?.takeIf { !it.getTime().isNaN() }
    return if (date != null && cell.status == ShotCellStatus.STALE) trf("screenshots.coverage.captured_date", "{description} Captured on {date}.",
        "description" to label, "date" to (date.asDynamic().toLocaleDateString(I18n.locale, js("({day:'numeric',month:'long'})")) as String)) else label
}

/** Setup progress reflects observed captures and design/delivery facts, never manual checkboxes. */
fun shotSetupSteps(hasPlan: Boolean, coverage: CoverageDto?, hasDesign: Boolean = false, hasDelivery: Boolean = false): Set<Int> = buildSet {
    if (hasPlan) add(0)
    if (coverage?.rows?.any { row -> row.cells.any { it.captureId != null && !it.blobMissing } } == true) add(1)
    if (hasDesign) add(2)
    if (coverage != null && coverage.rows.isNotEmpty() && coverage.totals.missing == 0 && coverage.totals.stale == 0) add(3)
    if (hasDelivery) add(4)
}

/** Polls quickly only for a recent agent upload, using the same ten-minute window on both screens. */
fun shotPollMillis(now: Double, lastAgentUpload: Double?): Long =
    if (lastAgentUpload != null && now - lastAgentUpload in 0.0..600_000.0) 10_000 else 60_000

/** Undo restores order by stable keys while retaining edits made since the original move. */
fun restoreShotOrder(slots: List<SlotInput>, previousKeys: List<String>): List<SlotInput> {
    val positions = previousKeys.withIndex().associate { it.value to it.index }
    return slots.sortedBy { positions[it.key] ?: Int.MAX_VALUE }
}

/** A cancelled pending deletion reinserts only the removed slot, leaving other drafts intact. */
fun restoreDeletedShot(slots: List<SlotInput>, deleted: SlotInput, position: Int): List<SlotInput> =
    if (slots.any { it.key == deleted.key }) slots else slots.toMutableList().apply { add(position.coerceIn(0, size), deleted) }

/** Stale reasons remain understandable when no changed-key sample exists. */
fun staleReasonLabel(reason: StaleReason): String = when (reason) {
    StaleReason.TEXT_CHANGED -> tr("app.projects.slug.screenshots.plan_model.interface_strings_changed_after_this_capture", "Interface strings changed after this capture.")
    StaleReason.TEXT_CHANGED_DURING_CAPTURE -> tr("app.projects.slug.screenshots.plan_model.interface_strings_changed_while_this_capture", "Capture inputs changed while this capture was being taken.")
    StaleReason.CAPTURE_INVALIDATED -> tr("app.projects.slug.screenshots.plan_model.this_capture_was_marked_for_retake", "This capture was marked for retake.")
    StaleReason.SLOT_INVALIDATED -> tr("app.projects.slug.screenshots.plan_model.every_capture_of_this_shot_was", "Every capture of this shot was marked for retake.")
}

/** Same-version re-renders preserve typing, while an explicit fresh server plan replaces the conflicted draft. */
fun reloadedShotDraft(previous: ScreenshotPlanDto, incoming: ScreenshotPlanDto, draft: List<SlotInput>): List<SlotInput> =
    if (previous == incoming) draft else incoming.editableSlots()

/** Read and plan failures must not be described as a failed image upload. */
fun shotRequestFailure(failure: Throwable): String = (failure as? dev.lingvo.site.api.ApiException)?.message
    ?: tr("app.projects.slug.screenshots.plan_model.couldnt_complete_this_screenshot_request_retry", "Couldn't complete this screenshot request. Retry when you're connected.")

/** Checklist completion is based on saved designs and successful delivery of the current valid approval. */
data class ShotSetupFacts(val hasDesign: Boolean = false, val hasDelivery: Boolean = false, val failure: String? = null)

/** A failed background refresh is reported without cancelling future actions; cancellation still retires it. */
suspend fun runShotRefresh(request: suspend () -> Unit, onFailure: (String) -> Unit) {
    try { request() }
    catch (cancelled: kotlinx.coroutines.CancellationException) { throw cancelled }
    catch (failure: Throwable) { onFailure(shotRequestFailure(failure)) }
}

/** The confirmation counts current and retained captures without claiming the caption survives as a live key. */
fun shotDeleteConsequence(count: Int): String = tPlural("screenshots.plan.delete_consequence", count,
    "Deletes the slot and its {count} capture. The caption key can be restored with the slot.",
    "Deletes the slot and its {count} captures. The caption key can be restored with the slot.")

/** Owns one visible-page wait and retires it on hide, replacement or unmount, including an in-flight read. */
class ShotVisiblePoller(
    private val scope: CoroutineScope,
    private val intervalMillis: () -> Long,
    private val request: suspend () -> Unit,
    private val onFailure: (String) -> Unit,
    private val wait: suspend (Long) -> Unit = { delay(it) },
) {
    private var job: Job? = null
    private var closed = false

    /** Repeated visibility events replace the owned wait instead of accumulating timers. */
    fun setVisible(visible: Boolean) {
        job?.cancel(); job = null
        if (visible && !closed) job = scope.launch(start = CoroutineStart.UNDISPATCHED) {
            while (isActive) {
                wait(intervalMillis())
                try { request() }
                catch (cancelled: CancellationException) { throw cancelled }
                catch (failure: Throwable) { onFailure(shotRequestFailure(failure)) }
            }
        }
    }

    /** Disposing a page also prevents a late visibility event from restarting its timer. */
    fun close() { closed = true; job?.cancel(); job = null }
}
